import axios from 'axios';
import { BASE_URL } from '../types/constants';
import { buildMarketingAttributionHeaders } from './marketing.service';
import type {
    VideoPurchaseCompleteResponse,
    VideoPurchaseCheckoutResponse,
    VideoPurchaseEntitlementsResponse
} from '../types/purchase.types';
import type { Video } from '../types/video.types';

/**
 * Create a checkout session for a single video purchase.
 * A marketing event id can be supplied for consent-aware server-side conversion dedupe.
 */
export const createVideoCheckout = async (
    videoId: string,
    token: string,
    marketingEventId?: string
): Promise<VideoPurchaseCheckoutResponse> => {
    const response = await axios.post<VideoPurchaseCheckoutResponse>(`${BASE_URL}/v1/purchase/video/checkout`, {
        VideoId: videoId
    }, {
        headers: {
            'Authorization': `Bearer ${token}`,
            ...buildMarketingAttributionHeaders(marketingEventId)
        }
    });

    return response.data;
};

/**
 * Verify and complete a returned Stripe Checkout session for a single video purchase.
 * A marketing event id can be supplied for consent-aware server/browser conversion dedupe.
 */
export const completeVideoPurchase = async (
    sessionId: string,
    token: string,
    marketingEventId?: string
): Promise<VideoPurchaseCompleteResponse> => {
    const response = await axios.post<VideoPurchaseCompleteResponse>(`${BASE_URL}/v1/purchase/video/complete`, {
        SessionId: sessionId
    }, {
        headers: {
            'Authorization': `Bearer ${token}`,
            ...buildMarketingAttributionHeaders(marketingEventId)
        }
    });

    return response.data;
};

/**
 * Fetch the list of owned video IDs for the current user.
 */
export const fetchVideoEntitlements = async (token: string): Promise<VideoPurchaseEntitlementsResponse> => {
    const response = await axios.get<VideoPurchaseEntitlementsResponse>(`${BASE_URL}/v1/purchase/entitlements`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    return response.data;
};

/**
 * Fetch purchased videos for the current user.
 */
export const fetchPurchasedVideos = async (token: string): Promise<Video[]> => {
    const response = await axios.get<Video[]>(`${BASE_URL}/v1/purchase/my`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    return response.data ?? [];
};
